import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  DocumentStatus,
  type DocumentCategory,
  type DocumentSubcategory,
  type DocumentTaxonomyResponse,
  type ListDocumentsFilters,
  type PropertyDocument,
} from '@/domain/entities/property-document';
import {
  archivePropertyDocument,
  createAndUploadPropertyDocument,
  deletePropertyDocument,
  fetchPropertyDocument,
  fetchPropertyDocumentDownloadUrl,
  fetchPropertyDocumentTaxonomy,
  listPropertyDocuments,
  replacePropertyDocumentFile,
  selectPropertyDocumentBucket,
  selectPropertyDocumentBucketState,
  selectPropertyDocumentByID,
  selectPropertyDocumentDeleting,
  selectPropertyDocumentDetail,
  selectPropertyDocumentTaxonomy,
  updatePropertyDocumentMetadata,
} from '@/features/property-documents';
import { usePermissions } from '@/hooks/usePermissions';
import { formatUnixDateTimeCompact } from '@/lib';
import { Permissions } from '@/lib/permissions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { TabLoadBanner } from './shared/TabLoadBanner';

type DocumentsTabProps = {
  propertyId: string;
};

const ALL = '__all__';

const STATUS_FILTERS: Array<{ value: DocumentStatus; label: string }> = [
  { value: DocumentStatus.Active, label: 'Active' },
  { value: DocumentStatus.Draft, label: 'Draft' },
  { value: DocumentStatus.PendingReview, label: 'Pending review' },
  { value: DocumentStatus.Archived, label: 'Archived' },
  { value: DocumentStatus.Superseded, label: 'Superseded' },
];

function labelFromEnumValue(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
}

function getDefaultTitle(file: File): string {
  return file.name.replace(/\.[^/.]+$/, '').trim();
}

function formatFileSize(sizeBytes?: number): string {
  if (typeof sizeBytes !== 'number') return 'Unknown size';
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
}

function getCategoryLabel(taxonomy: DocumentTaxonomyResponse | undefined, category?: DocumentCategory): string {
  if (!category) return 'All categories';
  return taxonomy?.items.find((item) => item.category === category)?.label ?? labelFromEnumValue(category);
}

function getSubcategoryLabel(
  taxonomy: DocumentTaxonomyResponse | undefined,
  category?: DocumentCategory,
  subcategory?: DocumentSubcategory,
): string {
  if (!subcategory) return 'All subcategories';
  const categoryItem = taxonomy?.items.find((item) => item.category === category);
  return (
    categoryItem?.subcategories.find((item) => item.subcategory === subcategory)?.label ??
    labelFromEnumValue(subcategory)
  );
}

function getStatusBadgeVariant(status: DocumentStatus): 'default' | 'secondary' | 'outline' {
  if (status === DocumentStatus.Active) return 'default';
  if (status === DocumentStatus.Archived || status === DocumentStatus.Superseded) return 'outline';
  return 'secondary';
}

function DocumentStatusBadge({ status }: { status: DocumentStatus }) {
  return <Badge variant={getStatusBadgeVariant(status)}>{labelFromEnumValue(status)}</Badge>;
}

function DocumentRow({
  document,
  categoryLabel,
  subcategoryLabel,
  onSelect,
}: {
  document: PropertyDocument;
  categoryLabel: string;
  subcategoryLabel: string;
  onSelect: () => void;
}) {
  return (
    <TableRow
      role="button"
      tabIndex={0}
      className="cursor-pointer"
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <TableCell>
        <div className="font-medium">{document.title}</div>
        {document.description ? (
          <div className="text-muted-foreground line-clamp-2 max-w-[520px] text-xs">{document.description}</div>
        ) : null}
      </TableCell>
      <TableCell className="text-muted-foreground">
        <div>{categoryLabel}</div>
        {document.subcategory ? <div className="text-xs">{subcategoryLabel}</div> : null}
      </TableCell>
      <TableCell>
        <DocumentStatusBadge status={document.status} />
      </TableCell>
      <TableCell className="text-muted-foreground">{document.documentType}</TableCell>
      <TableCell className="text-muted-foreground">{formatUnixDateTimeCompact(document.updatedAt)}</TableCell>
    </TableRow>
  );
}

export function DocumentsTab({ propertyId }: DocumentsTabProps) {
  const dispatch = useAppDispatch();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const taxonomy = useAppSelector((state) => selectPropertyDocumentTaxonomy(state, propertyId));
  const [category, setCategory] = useState<DocumentCategory | undefined>(undefined);
  const [subcategory, setSubcategory] = useState<DocumentSubcategory | undefined>(undefined);
  const [status, setStatus] = useState<DocumentStatus | undefined>(undefined);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState<DocumentCategory | undefined>(undefined);
  const [uploadSubcategory, setUploadSubcategory] = useState<DocumentSubcategory | undefined>(undefined);
  const [selectedDocumentID, setSelectedDocumentID] = useState<string | null>(null);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState<DocumentCategory | undefined>(undefined);
  const [editSubcategory, setEditSubcategory] = useState<DocumentSubcategory | undefined>(undefined);
  const [editStatus, setEditStatus] = useState<DocumentStatus>(DocumentStatus.Draft);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);

  const filters = useMemo<ListDocumentsFilters>(
    () => ({
      category,
      subcategory,
      status,
    }),
    [category, status, subcategory],
  );

  const documents = useAppSelector((state) => selectPropertyDocumentBucket(state, propertyId, filters));
  const bucketState = useAppSelector((state) => selectPropertyDocumentBucketState(state, propertyId, filters));
  const taxonomyLoading = useAppSelector((state) => state.propertyDocuments.loading);
  const globalError = useAppSelector((state) => state.propertyDocuments.error);
  const isUploading = useAppSelector((state) => state.propertyDocuments.uploading);
  const selectedDetail = useAppSelector((state) => selectPropertyDocumentDetail(state, selectedDocumentID));
  const selectedCachedDocument = useAppSelector((state) => selectPropertyDocumentByID(state, selectedDocumentID));
  const isDeletingSelected = useAppSelector((state) => selectPropertyDocumentDeleting(state, selectedDocumentID));
  const isLoading = Boolean(bucketState?.loading);
  const hasLoaded = Boolean(bucketState?.loaded);
  const error = bucketState?.error ?? globalError;
  const canUpload = isSuperAdmin || hasPermission(Permissions.DocumentsCreate);
  const canView = isSuperAdmin || hasPermission(Permissions.DocumentsRead);
  const canUpdate = isSuperAdmin || hasPermission(Permissions.DocumentsUpdate);
  const canDelete = isSuperAdmin || hasPermission(Permissions.DocumentsDelete);
  const selectedDocument = selectedDetail?.document ?? selectedCachedDocument;
  const detailLoading = Boolean(selectedDocumentID && taxonomyLoading && !selectedDetail);
  const selectedDocumentIsArchived = selectedDocument?.status === DocumentStatus.Archived;

  const categoryItems = taxonomy?.items ?? [];
  const selectedCategoryItem = categoryItems.find((item) => item.category === category);
  const subcategoryItems = selectedCategoryItem?.subcategories ?? [];
  const selectedUploadCategoryItem = categoryItems.find((item) => item.category === uploadCategory);
  const uploadSubcategoryItems = selectedUploadCategoryItem?.subcategories ?? [];
  const selectedEditCategoryItem = categoryItems.find((item) => item.category === editCategory);
  const editSubcategoryItems = selectedEditCategoryItem?.subcategories ?? [];

  useEffect(() => {
    if (!propertyId) return;
    if (taxonomy) return;
    void dispatch(fetchPropertyDocumentTaxonomy({ propertyID: propertyId, includeCounts: true }));
  }, [dispatch, propertyId, taxonomy]);

  useEffect(() => {
    if (!propertyId) return;
    if (bucketState?.loaded || bucketState?.loading) return;
    void dispatch(listPropertyDocuments({ propertyID: propertyId, filters }));
  }, [bucketState?.loaded, bucketState?.loading, dispatch, filters, propertyId]);

  useEffect(() => {
    if (!selectedDocumentID) return;
    void dispatch(fetchPropertyDocument({ propertyID: propertyId, documentID: selectedDocumentID }));
  }, [dispatch, propertyId, selectedDocumentID]);

  useEffect(() => {
    if (!selectedDocument) return;
    if (isEditingMetadata) return;
    setEditTitle(selectedDocument.title);
    setEditDescription(selectedDocument.description ?? '');
    setEditCategory(selectedDocument.category);
    setEditSubcategory(selectedDocument.subcategory ?? undefined);
    setEditStatus(selectedDocument.status);
  }, [isEditingMetadata, selectedDocument]);

  const refresh = () => {
    void dispatch(fetchPropertyDocumentTaxonomy({ propertyID: propertyId, includeCounts: true }));
    void dispatch(listPropertyDocuments({ propertyID: propertyId, filters, force: true }));
  };

  const openUploadDialog = () => {
    setUploadOpen(true);
    setUploadFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setUploadCategory(category);
    setUploadSubcategory(subcategory);
  };

  const onCategoryChange = (value: string) => {
    setCategory(value === ALL ? undefined : (value as DocumentCategory));
    setSubcategory(undefined);
  };

  const onSubcategoryChange = (value: string) => {
    setSubcategory(value === ALL ? undefined : value);
  };

  const onStatusChange = (value: string) => {
    setStatus(value === ALL ? undefined : (value as DocumentStatus));
  };

  const onUploadFileChange = (file: File | null) => {
    setUploadFile(file);
    if (file && !uploadTitle.trim()) {
      setUploadTitle(getDefaultTitle(file));
    }
  };

  const onUploadCategoryChange = (value: string) => {
    setUploadCategory(value === ALL ? undefined : (value as DocumentCategory));
    setUploadSubcategory(undefined);
  };

  const onUploadSubcategoryChange = (value: string) => {
    setUploadSubcategory(value === ALL ? undefined : value);
  };

  const onEditCategoryChange = (value: string) => {
    setEditCategory(value === ALL ? undefined : (value as DocumentCategory));
    setEditSubcategory(undefined);
  };

  const onEditSubcategoryChange = (value: string) => {
    setEditSubcategory(value === ALL ? undefined : value);
  };

  const beginEditMetadata = () => {
    if (!selectedDocument) return;
    setEditTitle(selectedDocument.title);
    setEditDescription(selectedDocument.description ?? '');
    setEditCategory(selectedDocument.category);
    setEditSubcategory(selectedDocument.subcategory ?? undefined);
    setEditStatus(selectedDocument.status);
    setIsEditingMetadata(true);
  };

  const cancelEditMetadata = () => {
    setIsEditingMetadata(false);
    if (!selectedDocument) return;
    setEditTitle(selectedDocument.title);
    setEditDescription(selectedDocument.description ?? '');
    setEditCategory(selectedDocument.category);
    setEditSubcategory(selectedDocument.subcategory ?? undefined);
    setEditStatus(selectedDocument.status);
  };

  const submitUpload = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canUpload) {
      toast.error('You do not have permission to upload documents.');
      return;
    }

    if (!uploadFile) {
      toast.error('Choose a document to upload.');
      return;
    }

    if (!uploadCategory) {
      toast.error('Choose a category.');
      return;
    }

    const title = uploadTitle.trim();
    if (!title) {
      toast.error('Title is required.');
      return;
    }

    try {
      await dispatch(
        createAndUploadPropertyDocument({
          propertyID: propertyId,
          file: uploadFile,
          metadata: {
            title,
            description: uploadDescription.trim() || null,
            category: uploadCategory,
            subcategory: uploadSubcategory ?? null,
          },
        }),
      ).unwrap();

      toast.success('Document uploaded.');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadTitle('');
      setUploadDescription('');
      setUploadCategory(undefined);
      setUploadSubcategory(undefined);
      void dispatch(fetchPropertyDocumentTaxonomy({ propertyID: propertyId, includeCounts: true }));
      void dispatch(listPropertyDocuments({ propertyID: propertyId, filters, force: true }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload document.');
    }
  };

  const viewSelectedDocument = async () => {
    if (!selectedDocumentID || !canView) return;

    try {
      const response = await dispatch(
        fetchPropertyDocumentDownloadUrl({ propertyID: propertyId, documentID: selectedDocumentID }),
      ).unwrap();
      window.open(response.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to open document.');
    }
  };

  const openReplaceDialog = () => {
    setReplacementFile(null);
    setReplaceOpen(true);
  };

  const replaceSelectedFile = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedDocumentID || !selectedDocument) return;

    if (!canUpdate) {
      toast.error('You do not have permission to update documents.');
      return;
    }

    if (!replacementFile) {
      toast.error('Choose a replacement file.');
      return;
    }

    try {
      await dispatch(
        replacePropertyDocumentFile({
          propertyID: propertyId,
          documentID: selectedDocumentID,
          file: replacementFile,
        }),
      ).unwrap();

      await dispatch(fetchPropertyDocument({ propertyID: propertyId, documentID: selectedDocumentID })).unwrap();
      void dispatch(listPropertyDocuments({ propertyID: propertyId, filters, force: true }));
      setReplaceOpen(false);
      setReplacementFile(null);
      toast.success('Document file replaced.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to replace document file.');
    }
  };

  const saveMetadata = async () => {
    if (!selectedDocumentID || !selectedDocument) return;

    if (!canUpdate) {
      toast.error('You do not have permission to update documents.');
      return;
    }

    const title = editTitle.trim();
    if (!title) {
      toast.error('Title is required.');
      return;
    }

    if (!editCategory) {
      toast.error('Choose a category.');
      return;
    }

    setIsSavingMetadata(true);
    try {
      await dispatch(
        updatePropertyDocumentMetadata({
          propertyID: propertyId,
          documentID: selectedDocumentID,
          metadata: {
            title,
            description: editDescription.trim() || null,
            category: editCategory,
            subcategory: editSubcategory ?? null,
            status: editStatus,
          },
        }),
      ).unwrap();

      await dispatch(fetchPropertyDocument({ propertyID: propertyId, documentID: selectedDocumentID })).unwrap();
      void dispatch(fetchPropertyDocumentTaxonomy({ propertyID: propertyId, includeCounts: true }));
      void dispatch(listPropertyDocuments({ propertyID: propertyId, filters, force: true }));
      setIsEditingMetadata(false);
      toast.success('Document updated.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update document.');
    } finally {
      setIsSavingMetadata(false);
    }
  };

  const archiveSelectedDocument = async () => {
    if (!selectedDocumentID || !selectedDocument) return;

    if (!canDelete) {
      toast.error('You do not have permission to archive documents.');
      return;
    }

    setIsArchiving(true);
    try {
      await dispatch(archivePropertyDocument({ propertyID: propertyId, documentID: selectedDocumentID })).unwrap();
      await dispatch(fetchPropertyDocument({ propertyID: propertyId, documentID: selectedDocumentID })).unwrap();
      void dispatch(fetchPropertyDocumentTaxonomy({ propertyID: propertyId, includeCounts: true }));
      void dispatch(listPropertyDocuments({ propertyID: propertyId, filters, force: true }));
      toast.success('Document archived.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to archive document.');
    } finally {
      setIsArchiving(false);
    }
  };

  const deleteSelectedDocument = async () => {
    if (!selectedDocumentID || !selectedDocument) return;

    if (!canDelete) {
      toast.error('You do not have permission to delete documents.');
      return;
    }

    if (!selectedDocumentIsArchived) {
      toast.error('Only archived documents can be deleted.');
      return;
    }

    try {
      await dispatch(deletePropertyDocument({ propertyID: propertyId, documentID: selectedDocumentID })).unwrap();
      setDeleteConfirmOpen(false);
      setSelectedDocumentID(null);
      setIsEditingMetadata(false);
      void dispatch(fetchPropertyDocumentTaxonomy({ propertyID: propertyId, includeCounts: true }));
      void dispatch(listPropertyDocuments({ propertyID: propertyId, filters, force: true }));
      toast.success('Document deleted.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete document.');
    }
  };

  const totalCount = taxonomy?.items.reduce((sum, item) => sum + (item.documentCount ?? 0), 0);
  const countLabel =
    typeof totalCount === 'number'
      ? `${documents.length} shown, ${totalCount} total`
      : hasLoaded
        ? `${documents.length} shown`
        : 'Not loaded yet';

  return (
    <div className="space-y-3">
      <TabLoadBanner
        mode="edit"
        isLoading={(taxonomyLoading && !taxonomy) || (isLoading && !hasLoaded)}
        error={error}
        loadingLabel="Loading documents..."
      />

      <Card>
        <CardHeader className="gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Documents</CardTitle>
            <div className="text-muted-foreground mt-1 text-sm">{countLabel}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            {canUpload ? (
              <Button onClick={openUploadDialog} disabled={isUploading || taxonomyLoading} className="gap-2">
                <Upload className="size-4" />
                Upload
              </Button>
            ) : null}
            <Button variant="outline" onClick={refresh} disabled={isLoading || taxonomyLoading} className="gap-2">
              <RefreshCw className="size-4" />
              Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-3">
            <Select value={category ?? ALL} onValueChange={onCategoryChange}>
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                {categoryItems.map((item) => (
                  <SelectItem key={item.category} value={item.category}>
                    {item.label}
                    {typeof item.documentCount === 'number' ? ` (${item.documentCount})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={subcategory ?? ALL} onValueChange={onSubcategoryChange} disabled={!category}>
              <SelectTrigger>
                <SelectValue placeholder="Subcategory" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All subcategories</SelectItem>
                {subcategoryItems.map((item) => (
                  <SelectItem key={item.subcategory} value={item.subcategory}>
                    {item.label}
                    {typeof item.documentCount === 'number' ? ` (${item.documentCount})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={status ?? ALL} onValueChange={onStatusChange}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {STATUS_FILTERS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                      {isLoading ? 'Loading documents...' : 'No documents found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((document) => (
                    <DocumentRow
                      key={document.documentID}
                      document={document}
                      categoryLabel={getCategoryLabel(taxonomy, document.category)}
                      subcategoryLabel={getSubcategoryLabel(
                        taxonomy,
                        document.category,
                        document.subcategory ?? undefined,
                      )}
                      onSelect={() => setSelectedDocumentID(document.documentID)}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={uploadOpen} onOpenChange={(open) => !isUploading && setUploadOpen(open)}>
        <DialogContent className="sm:max-w-[560px]">
          <form className="space-y-4" onSubmit={submitUpload}>
            <DialogHeader>
              <DialogTitle>Upload document</DialogTitle>
              <DialogDescription>Upload a file into this property vault.</DialogDescription>
            </DialogHeader>

            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="property-document-file">File</Label>
                <Input
                  id="property-document-file"
                  type="file"
                  disabled={isUploading}
                  onChange={(event) => onUploadFileChange(event.currentTarget.files?.[0] ?? null)}
                />
                {uploadFile ? (
                  <div className="text-muted-foreground text-xs">
                    {uploadFile.name} · {(uploadFile.size / (1024 * 1024)).toFixed(2)} MB
                  </div>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="property-document-title">Title</Label>
                <Input
                  id="property-document-title"
                  value={uploadTitle}
                  disabled={isUploading}
                  maxLength={120}
                  onChange={(event) => setUploadTitle(event.currentTarget.value)}
                  placeholder="Document title"
                />
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={uploadCategory ?? ALL} onValueChange={onUploadCategoryChange} disabled={isUploading}>
                    <SelectTrigger>
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>Select category</SelectItem>
                      {categoryItems.map((item) => (
                        <SelectItem key={item.category} value={item.category}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Subcategory</Label>
                  <Select
                    value={uploadSubcategory ?? ALL}
                    onValueChange={onUploadSubcategoryChange}
                    disabled={!uploadCategory || isUploading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Subcategory" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>No subcategory</SelectItem>
                      {uploadSubcategoryItems.map((item) => (
                        <SelectItem key={item.subcategory} value={item.subcategory}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="property-document-description">Description</Label>
                <Textarea
                  id="property-document-description"
                  value={uploadDescription}
                  disabled={isUploading}
                  maxLength={500}
                  onChange={(event) => setUploadDescription(event.currentTarget.value)}
                  placeholder="Optional notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" disabled={isUploading} onClick={() => setUploadOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedDocumentID)}
        onOpenChange={(open) => {
          if (open) return;
          if (isSavingMetadata) return;
          setSelectedDocumentID(null);
          setIsEditingMetadata(false);
        }}
      >
        <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.title ?? 'Document'}</DialogTitle>
            <DialogDescription>
              {selectedDocument
                ? getSubcategoryLabel(taxonomy, selectedDocument.category, selectedDocument.subcategory ?? undefined)
                : 'Loading document details...'}
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="text-muted-foreground py-8 text-sm">Loading document details...</div>
          ) : selectedDocument ? (
            <div className="space-y-5">
              {isEditingMetadata ? (
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="document-edit-title">Title</Label>
                    <Input
                      id="document-edit-title"
                      value={editTitle}
                      disabled={isSavingMetadata}
                      maxLength={120}
                      onChange={(event) => setEditTitle(event.currentTarget.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="document-edit-description">Description</Label>
                    <Textarea
                      id="document-edit-description"
                      value={editDescription}
                      disabled={isSavingMetadata}
                      maxLength={500}
                      onChange={(event) => setEditDescription(event.currentTarget.value)}
                    />
                  </div>

                  <div className="grid gap-3 md:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={editCategory ?? ALL}
                        onValueChange={onEditCategoryChange}
                        disabled={isSavingMetadata}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>Select category</SelectItem>
                          {categoryItems.map((item) => (
                            <SelectItem key={item.category} value={item.category}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Subcategory</Label>
                      <Select
                        value={editSubcategory ?? ALL}
                        onValueChange={onEditSubcategoryChange}
                        disabled={!editCategory || isSavingMetadata}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Subcategory" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>No subcategory</SelectItem>
                          {editSubcategoryItems.map((item) => (
                            <SelectItem key={item.subcategory} value={item.subcategory}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={editStatus}
                        onValueChange={(value) => setEditStatus(value as DocumentStatus)}
                        disabled={isSavingMetadata}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_FILTERS.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ) : selectedDocument.description ? (
                <div className="space-y-1">
                  <Label>Description</Label>
                  <div className="text-muted-foreground text-sm whitespace-pre-wrap">
                    {selectedDocument.description}
                  </div>
                </div>
              ) : null}

              <div className="grid gap-3 text-sm md:grid-cols-2">
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground text-xs">Category</div>
                  <div className="font-medium">{getCategoryLabel(taxonomy, selectedDocument.category)}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground text-xs">Status</div>
                  <div className="pt-1">
                    <DocumentStatusBadge status={selectedDocument.status} />
                  </div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground text-xs">Type</div>
                  <div className="font-medium">{selectedDocument.documentType}</div>
                </div>
                <div className="rounded-md border p-3">
                  <div className="text-muted-foreground text-xs">Updated</div>
                  <div className="font-medium">{formatUnixDateTimeCompact(selectedDocument.updatedAt)}</div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="font-medium">Versions</div>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Version</TableHead>
                        <TableHead>File</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Uploaded</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedDetail?.versions?.length ? (
                        selectedDetail.versions.map((version) => (
                          <TableRow key={version.versionID}>
                            <TableCell>
                              v{version.versionNumber}
                              {version.isLatest ? (
                                <span className="text-muted-foreground ml-1 text-xs">latest</span>
                              ) : null}
                            </TableCell>
                            <TableCell className="max-w-[220px] truncate">{version.fileName}</TableCell>
                            <TableCell className="text-muted-foreground">{version.uploadStatus}</TableCell>
                            <TableCell className="text-muted-foreground">{formatFileSize(version.sizeBytes)}</TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatUnixDateTimeCompact(version.uploadedAt)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="text-muted-foreground h-16 text-center">
                            No versions loaded.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="space-y-2">
                <div className="font-medium">Links</div>
                {selectedDetail?.links?.length ? (
                  <div className="grid gap-2">
                    {selectedDetail.links.map((link) => (
                      <div key={link.linkID} className="rounded-md border p-3 text-sm">
                        <div className="font-medium">
                          {link.entityType} · {link.relationType}
                        </div>
                        <div className="text-muted-foreground text-xs">{link.entityID}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted-foreground rounded-md border p-3 text-sm">No entity links.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground py-8 text-sm">Document details are unavailable.</div>
          )}

          <DialogFooter>
            {isEditingMetadata ? (
              <>
                <Button type="button" variant="outline" disabled={isSavingMetadata} onClick={cancelEditMetadata}>
                  Cancel
                </Button>
                <Button type="button" disabled={isSavingMetadata} onClick={saveMetadata}>
                  {isSavingMetadata ? 'Saving...' : 'Save changes'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSelectedDocumentID(null)}>
                  Close
                </Button>
                {canUpdate ? (
                  <Button variant="outline" disabled={!selectedDocument || detailLoading} onClick={beginEditMetadata}>
                    Edit metadata
                  </Button>
                ) : null}
                {canUpdate ? (
                  <Button
                    variant="outline"
                    disabled={!selectedDocument || detailLoading || isUploading}
                    onClick={openReplaceDialog}
                  >
                    Replace file
                  </Button>
                ) : null}
                {canDelete && selectedDocument && !selectedDocumentIsArchived ? (
                  <Button
                    variant="outline"
                    disabled={detailLoading || isArchiving}
                    onClick={() => void archiveSelectedDocument()}
                  >
                    {isArchiving ? 'Archiving...' : 'Archive'}
                  </Button>
                ) : null}
                {canDelete && selectedDocumentIsArchived ? (
                  <Button
                    variant="destructive"
                    disabled={detailLoading || isDeletingSelected}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    Delete
                  </Button>
                ) : null}
                <Button onClick={viewSelectedDocument} disabled={!selectedDocument || detailLoading || !canView}>
                  View document
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={replaceOpen} onOpenChange={(open) => !isUploading && setReplaceOpen(open)}>
        <DialogContent className="sm:max-w-[520px]">
          <form className="space-y-4" onSubmit={replaceSelectedFile}>
            <DialogHeader>
              <DialogTitle>Replace file</DialogTitle>
              <DialogDescription>
                Upload a new file version for{' '}
                {selectedDocument?.title ? `"${selectedDocument.title}"` : 'this document'}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="property-document-replacement-file">File</Label>
              <Input
                id="property-document-replacement-file"
                type="file"
                disabled={isUploading}
                onChange={(event) => setReplacementFile(event.currentTarget.files?.[0] ?? null)}
              />
              {replacementFile ? (
                <div className="text-muted-foreground text-xs">
                  {replacementFile.name} · {formatFileSize(replacementFile.size)}
                </div>
              ) : null}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" disabled={isUploading} onClick={() => setReplaceOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isUploading}>
                {isUploading ? 'Replacing...' : 'Replace file'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => !isDeletingSelected && setDeleteConfirmOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete archived document?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the document metadata, versions, links, audit history, and stored files. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSelected}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              disabled={isDeletingSelected}
              onClick={(event) => {
                event.preventDefault();
                void deleteSelectedDocument();
              }}
            >
              {isDeletingSelected ? 'Deleting...' : 'Delete document'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
